/* eslint-disable tss-unused-classes/unused-classes */
import { makeStyles } from 'tss-react/mui';
import { CustomFilter } from 'app/ComponentLibrary/Filter';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { useDispatch, useSelector } from 'react-redux';
import { selectGridFiltersExpanded } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { useFormContext } from 'react-hook-form';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import {
  defaultFilterValues,
  FacilityOrderPayPackagesFilterSpecs,
} from './filterUtils';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
});

export const FacilityOrderPayPackagesFilters = ({
  saveFilterPreferences,
  facilityId,
}: {
  saveFilterPreferences?: (filterValues: any, newSelectedChipFilters?: any) => void;
  facilityId: number;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset } = useFormContext();

  const sourceRows = useSelector((state: any) => state?.orderData?.orderPayPackagesSource ?? []);

  const gridSpecs = useRef(
    new FacilityOrderPayPackagesFilterSpecs(
      {
        orderIdOpts: [],
        weeklyGrossMarginOpts: [],
        shiftOpts: [],
        postingStatusOpts: [],
      },
      t,
    ),
  );
  const [fields, setFields] = useState<any>(gridSpecs.current.getSpecs());

  useEffect(() => {
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) return;

    const orderIdSet = new Set<string>();
    const marginSet = new Set<string>();
    const shiftSet = new Set<string>();
    const postingStatusSet = new Set<string>();

    sourceRows.forEach(r => {
      const oid = r?.orderId ?? r?.orderID;
      if (oid != null && `${oid}`.length > 0) orderIdSet.add(String(oid));

      const margin = r?.currentTargetMargin;
      if (margin != null && !Number.isNaN(Number(margin))) {
        marginSet.add(`${Number(margin).toFixed(2)}%`);
      }

      const shift = `${r?.shift ?? ''}`.trim();
      if (shift) shiftSet.add(shift);

      const status = `${r?.publishingStatus ?? ''}`.trim();
      if (status) postingStatusSet.add(status);
    });

    gridSpecs.current.filterSpecs.orderIdOpts = Array.from(orderIdSet)
      .sort((a, b) => Number(a) - Number(b))
      .map((v, idx) => ({ id: idx + 1, name: v, value: v }));

    gridSpecs.current.filterSpecs.weeklyGrossMarginOpts = Array.from(marginSet)
      .sort((a, b) => parseFloat(String(a).replace('%', '')) - parseFloat(String(b).replace('%', '')))
      .map((v, idx) => ({ id: idx + 1, name: v, value: v }));

    gridSpecs.current.filterSpecs.shiftOpts = Array.from(shiftSet)
      .sort()
      .map((v, idx) => ({ id: idx + 1, name: v, value: v }));

    gridSpecs.current.filterSpecs.postingStatusOpts = Array.from(postingStatusSet)
      .sort()
      .map((v, idx) => ({ id: idx + 1, name: v, value: v }));

    setFields(gridSpecs.current.getSpecs());
  }, [sourceRows]);

  const resetFilter = () => {
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(defaultFilterValues),
        selectedChipFilters: null,
      }),
    );
    reset({ ...defaultFilterValues });
    if (saveFilterPreferences) {
      saveFilterPreferences(defaultFilterValues, null);
    }
  };

  const refreshData = () => {
    if (Number(facilityId) > 0) {
      dispatch(orderDataActions.getOrderPayPackages({ facilityId: Number(facilityId) }));
    }
  };

  const filtersExpanded = useSelector(selectGridFiltersExpanded);
  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
          <CustomFilter fields={fields} justifyContent={'flex-start'} version2 resetBtn onResetForm={resetFilter} />
        </Collapse>
      </Grid>
      <Grid item xs={1} className={classes.fillterCollapse}>
        <SearchPageIcons
          resetlbutton
          funnelButton
          onResetForm={() => refreshData()}
          onAdvancedFilterClick={() => {
            resetFilter();
            refreshData();
            handleChange();
          }}
          checked={filtersExpanded}
        />
      </Grid>
    </Grid>
  );
};
